/* eslint-disable no-unused-vars */
/* eslint-disable no-unused-expressions */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect, useCallback } from "react";
import styles from "./careTeamIntake.module.css";
import { CARE_SEARCH_PORVIDER, CARE_TEAM_LOG, CARE_TEAM_SAVE } from "../GSA_CONSTANTS";
// import { ajaxLink } from "../CONSTANTS";

// NPPES NPI Registry — public CMS API, no key required. version=2.1 required.
const NPPES_BASE = "https://npiregistry.cms.hhs.gov/api/";

const STORAGE_KEY = "virdio_careteam_intake";

// Section 4.05(B): "The record holds the version of the consent language
// shown, the timestamp and the decision. It is a stored version reference,
// not a boolean flag." These are the single source of truth for both the
// on-screen copy and the wording recorded against the decision, so the two
// can never drift apart.
const CARE_TEAM_CONSENT_WORDING_VERSION = "careteam-share-consent-2026-08-11-v1";
const CARE_TEAM_CONSENT_DISCLOSURE = "We will send a summary of your starting measurements, your care plan, and how you are progressing. We will not share anything else.";
const CARE_TEAM_CONSENT_CHECKBOX_LABEL = "Yes, please share my progress with this clinician";

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "");

const END_STATES = {
  declined: {
    title: "Thank you. That's all we needed.",
    body: "You don't need to do anything else here. If that changes later, you can add a clinician from your account at any time.",
  },
  unknown: {
    title: "Thank you. That's all we needed.",
    body: "You don't need to do anything else here. If you think of someone later, you can add them from your account at any time.",
  },
  skipped: {
    title: "That's fine. We've moved on.",
    body: "You can add a clinician from your account whenever you like.",
  },
  nomatch: {
    title: "Thank you. We couldn't confirm those details.",
    body: "That's not a problem, and it doesn't affect your care. You can add your clinician's details from your account at any time.",
  },
  noconsent: {
    title: "Understood. We won't share anything.",
    body: "Nothing will be sent to anyone. You can still see all of your own progress in your account, and you can change your mind at any time.",
  },
};

export default function CareTeamIntake({ selectedState = "", sessionId = "", onContinue = () => {}, onAnalyticsEvent = () => {} }) {
  // screen: 'ask' | 'search' | 'results' | 'consent' | 'success' | 'end'
  const [screen, setScreen] = useState("ask");
  const [hasCareTeam, setHasCareTeam] = useState(null);
  const [askError, setAskError] = useState("");
  const hasCareTeamRef = React.useRef(null);

  const [searchForm, setSearchForm] = useState({
    firstName: "",
    lastName: "",
    state: selectedState || "",
  });
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const [consentChecked, setConsentChecked] = useState(false);
  const [endKind, setEndKind] = useState("");
  const [savedClinician, setSavedClinician] = useState(null);

  const [eventLog, setEventLog] = useState([]);

  // ---- rehydrate on mount (survives a refresh mid-flow, same pattern as
  // virdio_lbp_preferences in the parent) ----
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (saved?.eventLog) setEventLog(saved.eventLog);
    } catch (e) {
      // ignore corrupt storage
    }
  }, []);

  // ---- persist every change locally, mirroring the parent's savePreferences pattern ----
  useEffect(() => {
    const data = {
      hasCareTeam,
      searchForm,
      selectedIndex,
      results: results.map((r) => ({ npi: r.npi, name: `${r.firstName} ${r.lastName}` })),
      consentChecked,
      endKind,
      savedClinician,
      eventLog,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [hasCareTeam, searchForm, selectedIndex, results, consentChecked, endKind, savedClinician, eventLog]);

  const recordEvent = useCallback(
    (event, detail = {}) => {
      const entry = { event, detail, timestamp: new Date().toISOString() };
      setEventLog((prev) => [...prev, entry]);
      onAnalyticsEvent(event, typeof detail?.count === "number" ? detail.count : null);
      if (!sessionId) {
        console.warn("[CareTeamIntake] No sessionId available; log event kept locally only.");
        return;
      }
      fetch(CARE_TEAM_LOG, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, event, detail, timestamp: entry.timestamp }),
      }).catch((err) => console.warn("[CareTeamIntake] log call failed (non-blocking):", err));
    },
    [sessionId, onAnalyticsEvent],
  );

  const saveClinicianRecord = useCallback(
    async (clinician, status, consent = null) => {
      if (!sessionId) {
        console.warn("[CareTeamIntake] No sessionId available; skipping clinician save.");
        return;
      }
      try {
        await fetch(CARE_TEAM_SAVE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId,
            clinician,
            status,
            at: new Date().toISOString(),
            ...(consent
              ? {
                  consentWordingVersion: consent.consentWordingVersion,
                  consentWording: consent.consentWording,
                }
              : {}),
          }),
        });
      } catch (err) {
        console.warn("[CareTeamIntake] save-clinician call failed (non-blocking):", err);
      }
    },
    [sessionId],
  );

  const finishEnd = (kind) => {
    setEndKind(kind);
    setScreen("end");
  };

  // ---- Screen: ask ---------------------------------------------------
  const selectHasCareTeam = (val) => {
    hasCareTeamRef.current = val;
    setHasCareTeam(val);
    setAskError("");
  };

  const chooseHasCareTeam = () => {
    const chosen = hasCareTeamRef.current;
    if (!chosen) {
      setAskError("Please select one of the options above.");
      return;
    }
    setAskError("");
    if (chosen === "yes") {
      recordEvent("care_team_has_clinician_yes");
      setSearchForm((f) => ({ ...f, firstName: "", lastName: "", state: selectedState || f.state }));
      setScreen("search");
    } else if (chosen === "no") {
      recordEvent("care_team_declined", { wording: "No, I don't have anyone" });
      finishEnd("declined");
    } else {
      recordEvent("care_team_unsure", { wording: "I'm not sure" });
      finishEnd("unknown");
    }
  };

  // ---- Screen: search --------------------------------------------------
  const updateSearchField = (field, value) => setSearchForm((f) => ({ ...f, [field]: value }));

  const handleSkipSearch = () => {
    recordEvent("care_team_search_skipped");
    finishEnd("skipped");
  };

  const runSearch = async () => {
    setSearchError("");
    if (!searchForm.lastName.trim()) {
      setSearchError("Please enter the clinician's last name.");
      return;
    }
    setSearching(true);
    recordEvent("care_team_search_submitted", { ...searchForm });
    try {
      const params = new URLSearchParams({});
      if (searchForm.firstName.trim()) params.set("firstName", searchForm.firstName.trim());
      params.set("lastName", searchForm.lastName.trim());
      if (searchForm.state) {
        params.set("state", searchForm.state);
      }

      const res = await fetch(`${CARE_SEARCH_PORVIDER}?${params.toString()}`);
      const body = await res.json();
      if (!res.ok) {
        throw new Error(body?.error || "The provider directory could not run that search.");
      }
      const mapped = body?.results || [];
      setResults(mapped);
      setSelectedIndex(null);
      recordEvent("care_team_search_results", { count: mapped.length, npis: mapped.map((m) => m.npi) });
      setScreen("results");
    } catch (err) {
      console.error("[CareTeamIntake] provider search failed:", err);
      setSearchError("We couldn't reach the provider directory. Please try again in a moment.");
      recordEvent("care_team_search_error", { message: err.message });
    } finally {
      setSearching(false);
    }
  };

  // ---- Screen: results --------------------------------------------------
  const pickResult = (idx) => setSelectedIndex(idx);

  const confirmClinicianMatch = async () => {
    if (selectedIndex === null) return;
    const r = results[selectedIndex];
    recordEvent("care_team_clinician_selected", { npi: r.npi, name: `${r.firstName} ${r.lastName}` });
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      saved.selectedClinician = r;
      saved.clinicianConfirmedAt = new Date().toISOString();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
    } catch (e) {
      // non-fatal
    }
    // "If we find them, we need to store that information with the
    // beneficiary" — this happens on confirmation of the match, before
    // (and independent of) the consent-to-share decision below.
    await saveClinicianRecord(r, "matched");
    setScreen("consent");
  };

  const noneMatchClicked = () => {
    recordEvent("care_team_no_match", { candidatesShown: results.map((r) => r.npi) });
    finishEnd("nomatch");
  };

  // ---- Screen: consent --------------------------------------------------
  const declineShare = async () => {
    const r = results[selectedIndex];
    // Defect 5 fix: the same version reference and wording that go to the
    // analytics event now also go to the clinician record itself.
    const consent = {
      consentWordingVersion: CARE_TEAM_CONSENT_WORDING_VERSION,
      consentWording: `${CARE_TEAM_CONSENT_DISCLOSURE} ${CARE_TEAM_CONSENT_CHECKBOX_LABEL}`,
    };
    recordEvent("care_team_consent_declined", { npi: r?.npi, ...consent });
    if (r) await saveClinicianRecord(r, "consent-declined", consent);
    finishEnd("noconsent");
  };

  const confirmShare = async () => {
    if (!consentChecked) return;
    const r = results[selectedIndex];
    const consent = {
      consentWordingVersion: CARE_TEAM_CONSENT_WORDING_VERSION,
      consentWording: `${CARE_TEAM_CONSENT_DISCLOSURE} ${CARE_TEAM_CONSENT_CHECKBOX_LABEL}`,
    };
    recordEvent("care_team_consent_given", { npi: r.npi, ...consent });
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      saved.consentGiven = true;
      saved.consentGivenAt = new Date().toISOString();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
    } catch (e) {
      // non-fatal
    }
    await saveClinicianRecord(r, "consent-given", consent);
    // Actually transmitting the eFax is a later build step (Section
    // 4.05(D)); this event marks that the send has been queued/expected,
    // it does not perform the send itself.
    recordEvent("care_team_send_queued", { npi: r.npi, method: r.hasDirect ? "direct-secure-message, fax fallback" : "fax" });
    setSavedClinician(r);
    setScreen("success");
  };

  // ===================================================================
  // Render
  // ===================================================================
  return (
    <div className={styles.careTeamIntake}>
      {screen === "ask" && (
        <div className={styles.screen}>
          <div className={styles.eyebrow}>Your care team</div>
          <h2 className={styles.h1}>Is there a doctor or clinician who helps you with your back pain?</h2>
          <p className={styles.lede}>If there is, we can send them a short update about your progress. This is optional, and it will not change your care with us either way.</p>

          <div className={styles.stack}>
            <button type="button" className={`${styles.choice} ${hasCareTeam === "yes" ? styles.choiceSel : ""} ${askError ? styles.choiceError : ""}`} onClick={() => selectHasCareTeam("yes")}>
              <span className={styles.choiceTitle}>Yes, I have someone</span>
              <span className={styles.choiceSub}>A primary care doctor, a specialist, or another clinician</span>
            </button>
            <button type="button" className={`${styles.choice} ${hasCareTeam === "no" ? styles.choiceSel : ""} ${askError ? styles.choiceError : ""}`} onClick={() => selectHasCareTeam("no")}>
              <span className={styles.choiceTitle}>No, I don't have anyone</span>
              <span className={styles.choiceSub}>You are not seeing a doctor or clinician about this</span>
            </button>
            <button type="button" className={`${styles.choice} ${hasCareTeam === "unsure" ? styles.choiceSel : ""} ${askError ? styles.choiceError : ""}`} onClick={() => selectHasCareTeam("unsure")}>
              <span className={styles.choiceTitle}>I'm not sure</span>
              <span className={styles.choiceSub}>You would rather not say, or you don't know who to name</span>
            </button>
          </div>

          {askError && <div className={styles.errmsg}>{askError}</div>}

          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={chooseHasCareTeam}>
              Submit
            </button>
          </div>
        </div>
      )}

      {screen === "search" && (
        <div className={styles.screen}>
          <div className={styles.eyebrow}>Your care team</div>
          <h2 className={styles.h1}>Who is your doctor or clinician?</h2>
          <p className={styles.lede}>We will look them up in the national provider directory. If you have an after-visit summary or an appointment card handy, the details are usually on it.</p>

          <div className={styles.field}>
            <label htmlFor="ct-fn">
              First name <span className={styles.hint}>— optional</span>
            </label>
            <input id="ct-fn" type="text" value={searchForm.firstName} onChange={(e) => updateSearchField("firstName", e.target.value)} autoComplete="off" />
          </div>
          <div className={styles.field}>
            <label htmlFor="ct-ln">Last name</label>
            <input id="ct-ln" type="text" value={searchForm.lastName} onChange={(e) => updateSearchField("lastName", e.target.value)} autoComplete="off" required />
          </div>
          <div className={styles.field}>
            <label htmlFor="ct-st">State</label>
            <input id="ct-st" type="text" value={searchForm.state} readOnly />
          </div>

          {searchError && <div className={styles.errmsg}>{searchError}</div>}

          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={runSearch} disabled={searching}>
              {searching ? "Searching…" : "Search"}
            </button>
            <button type="button" className={styles.btnQuiet} onClick={handleSkipSearch}>
              I'd rather skip this
            </button>
          </div>
        </div>
      )}

      {screen === "results" && (
        <div className={styles.screen}>
          <div className={styles.eyebrow}>Your care team</div>
          {results.length > 0 && <h2 className={styles.h1}>Is one of these your clinician?</h2>}
          <p className={styles.lede}>{results.length ? `We found ${results.length} possible ${results.length === 1 ? "match" : "matches"}. Choose the right one, or tell us none of them is correct.` : "We didn't find a match. Tell us none of them is correct and we'll note that for your record."}</p>

          <div className={styles.stack}>
            {results.map((r, idx) => (
              <button type="button" key={r.npi} className={`${styles.result} ${selectedIndex === idx ? styles.resultSel : ""}`} onClick={() => pickResult(idx)}>
                <span className={styles.radio} />
                <span className={styles.resultBody}>
                  <span className={styles.resultName}>
                    {titleCase(r.firstName)} {titleCase(r.lastName)}
                    {r.credential ? `, ${r.credential}` : ""}
                  </span>
                  <span className={styles.resultSpecialty}>
                    {r.specialty} {r.specialty ? "· " : ""}NPI {r.npi}
                  </span>
                  <span className={styles.resultAddress}>
                    {r.addressLine}
                    <br />
                    {r.city}, {r.state} {r.postalCode}
                    {r.phone ? ` · Phone ${r.phone}` : ""}
                  </span>
                  <span className={`${styles.resultTag} ${!r.hasDirect ? styles.resultTagMuted : ""}`}>{r.hasDirect ? "Secure message and fax on file" : r.fax ? `Fax on file · ${r.fax}` : "No routing information on file"}</span>
                </span>
              </button>
            ))}
          </div>

          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={confirmClinicianMatch} disabled={selectedIndex === null}>
              This is my clinician
            </button>
            <button type="button" className={styles.btnQuiet} onClick={noneMatchClicked}>
              None of these is my clinician
            </button>
          </div>
        </div>
      )}

      {screen === "consent" && selectedIndex !== null && (
        <div className={styles.screen}>
          <div className={styles.eyebrow}>Your care team</div>
          <h2 className={styles.h1}>May we share your progress with them?</h2>
          <p className={styles.lede}>We would send a short summary at three points: when your programme starts, if your care changes, and when your programme ends. Nothing is shared without your permission.</p>

          <div className={styles.panel}>
            <h3 className={styles.panelTitle}>
              {titleCase(results[selectedIndex].firstName)} {titleCase(results[selectedIndex].lastName)}
              {results[selectedIndex].credential ? `, ${results[selectedIndex].credential}` : ""}
            </h3>
            <dl className={styles.kv}>
              <dt>Specialty</dt>
              <dd>{results[selectedIndex].specialty || "—"}</dd>
              <dt>NPI</dt>
              <dd>{results[selectedIndex].npi}</dd>
              <dt>Practice address</dt>
              <dd>
                {results[selectedIndex].addressLine}, {results[selectedIndex].city}, {results[selectedIndex].state} {results[selectedIndex].postalCode}
              </dd>
              <dt>We would fax to</dt>
              <dd>{results[selectedIndex].fax || "Not on file"}</dd>
            </dl>
          </div>

          <div className={styles.consentBox}>
            <p>{CARE_TEAM_CONSENT_DISCLOSURE}</p>
            <label className={`${styles.check} ${consentChecked ? styles.checkOn : ""}`}>
              <input type="checkbox" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} />
              <span className={styles.box} />
              <span className={styles.checkTxt}>{CARE_TEAM_CONSENT_CHECKBOX_LABEL}</span>
            </label>
          </div>

          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={confirmShare} disabled={!consentChecked}>
              Confirm
            </button>
            <button type="button" className={styles.btnQuiet} onClick={declineShare}>
              No, please don't share
            </button>
          </div>
        </div>
      )}

      {screen === "success" && savedClinician && (
        <div className={styles.screen}>
          <div className={styles.doneMark} />
          <div className={styles.eyebrow}>Your care team</div>
          <h2 className={styles.h1}>Thank you. We have your clinician's details.</h2>
          <p className={styles.lede}>We will send them your first update once your starting measurements are complete. You will be able to see everything we send from your account at any time.</p>

          <div className={styles.panel}>
            <h3 className={styles.panelTitle}>What we recorded</h3>
            <dl className={styles.kv}>
              <dt>Clinician</dt>
              <dd>
                {titleCase(savedClinician.firstName)} {titleCase(savedClinician.lastName)}
                {savedClinician.credential ? `, ${savedClinician.credential}` : ""}
              </dd>
              <dt>NPI verified</dt>
              <dd>{savedClinician.npi} · NPPES</dd>
              <dt>Licence</dt>
              <dd>
                {savedClinician.license || "—"} {savedClinician.licenseState ? `(${savedClinician.licenseState})` : ""}
              </dd>
              <dt>Delivery method</dt>
              <dd>{savedClinician.hasDirect ? `Direct secure message, fax fallback to ${savedClinician.fax || "—"}` : `Secure fax to ${savedClinician.fax || "—"}`}</dd>
              <dt>Permission to share</dt>
              <dd>Given</dd>
            </dl>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={() => {
                onContinue("consent-given");
                // setScreen("ask");
              }}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {screen === "end" && (
        <div className={styles.screen}>
          <div className={styles.doneMark} />
          <div className={styles.eyebrow}>Your care team</div>
          <h2 className={styles.h1}>{END_STATES[endKind]?.title || "Thank you."}</h2>
          <p className={styles.lede}>{END_STATES[endKind]?.body || ""}</p>

          <div className={styles.actions}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={() => {
                onContinue(endKind);
                // setScreen("ask");
              }}
            >
              Continue
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
